import { FC, useState, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ToastMessageContext } from '@context';
import { useTranslation } from 'react-i18next';
import { T_KEYS } from '../../constants';
import { 
  useCheckBookingConnectionMutation,
  useValidateBookingHotelIdsMutation,
  useChannelUnitsForImportQuery,
  useImportUnitsMutation,
} from '../../hooks';
import { BookingUnitType, IHotelValidationResult, IChannelProperty } from '../../types';
import { BookingPropertyIdStep } from '../ConnectionSteps';
import { ImportPropertiesStep } from '../ImportSteps';
import { ImportingModal } from '../Modals';

type ModalStep = 'propertyId' | 'import';

interface AddBookingPropertyModalProps {
  open: boolean;
  onClose: () => void;
  connectionType: number; // 1 = Single, 2 = Multi
  mainChannelId: number; // Parent account ID for sub-accounts
  onSuccess?: () => void;
}

/**
 * AddBookingPropertyModal Component
 * 
 * Modal for adding a new Booking property to an existing account.
 * Skips channel selection and unit type selection since connectionType is already known.
 * Flow: PropertyIdStep -> ImportPropertiesStep
 */
const AddBookingPropertyModal: FC<AddBookingPropertyModalProps> = ({
  open,
  onClose,
  connectionType,
  mainChannelId,
  onSuccess,
}) => {
  const { t, i18n: { language } } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Modal step state
  const [currentStep, setCurrentStep] = useState<ModalStep>('propertyId');
  
  // Booking property ID state
  const [bookingError, setBookingError] = useState<string | undefined>();
  const [bookingValidationResults, setBookingValidationResults] = useState<IHotelValidationResult[] | undefined>();
  const [hasBookingValidationErrors, setHasBookingValidationErrors] = useState(false);
  
  // Import state
  const [channelId, setChannelId] = useState<string | null>(null);
  const [properties, setProperties] = useState<IChannelProperty[]>([]);
  const [importingIds, setImportingIds] = useState<string[]>([]);
  const [showImportingModal, setShowImportingModal] = useState(false);

  // Convert connectionType number to BookingUnitType
  const unitType: BookingUnitType = connectionType === 1 ? 'single' : 'multi';

  // Mutations
  const { mutate: checkBookingConnection, isPending: isCheckingBooking } = useCheckBookingConnectionMutation();
  const { mutate: validateBookingHotelIds, isPending: isValidatingBookingIds } = useValidateBookingHotelIdsMutation();
  const { mutate: importUnits, isPending: isImporting } = useImportUnitsMutation();

  // Fetch units for import when channelId is set
  const { 
    data: unitsData, 
    isLoading: isUnitsLoading,
    refetch: refetchUnits,
  } = useChannelUnitsForImportQuery('booking', channelId);

  // Extract userChannelId and channelType from the query result
  const userChannelId = unitsData?.userChannelId ?? null;
  const apiChannelType = unitsData?.channelType ?? null;

  // Transform units data to IChannelProperty when data arrives
  useEffect(() => {
    if (unitsData?.units) {
      setProperties(
        unitsData.units.map((unit) => ({
          id: unit.id,
          externalId: unit.propertyId,
          name: unit.name,
          location: unit.address,
          imageUrl: unit.imageUrl || undefined,
          isImported: unit.isSynced,
          channelName: 'booking' as const,
        }))
      );
    }
  }, [unitsData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentStep('propertyId');
      setBookingError(undefined);
      setBookingValidationResults(undefined);
      setHasBookingValidationErrors(false);
      setChannelId(null);
      setProperties([]);
      setImportingIds([]);
    }
  }, [open]);

  // Handle Booking property IDs submission (for single unit type - multiple IDs)
  const handleBookingPropertyIdsSubmit = useCallback((propertyIds: string[]) => {
    setBookingError(undefined);
    setBookingValidationResults(undefined);
    setHasBookingValidationErrors(false);

    validateBookingHotelIds(
      { hotelIds: propertyIds, mainChannelId },
      {
        onSuccess: (data) => {
          if (data.invalidCount > 0) {
            setBookingValidationResults(data.results ?? undefined);
            setHasBookingValidationErrors(true);
            showError(t(T_KEYS.SOME_PROPERTY_IDS_INVALID));
          } else {
            showSuccess(t(T_KEYS.VALIDATION_SUCCESSFUL));
            const hotelId = data.hotelId;
            setChannelId(hotelId);
            setCurrentStep('import');
          }
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          setBookingError(errorMsg || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [validateBookingHotelIds, mainChannelId, showSuccess, showError, isRtl, t]);

  // Handle Booking single property ID submission (for multi unit type)
  const handleBookingSinglePropertyIdSubmit = useCallback((propertyId: string) => {
    setBookingError(undefined);

    checkBookingConnection(
      { hotelId: propertyId, connectionType, mainChannelId },
      {
        onSuccess: (data) => {
          showSuccess(isRtl ? data.messageAr : data.message);
          const hotelId = data.hotelId;
          setChannelId(hotelId || propertyId);
          setCurrentStep('import');
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          setBookingError(errorMsg || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [checkBookingConnection, connectionType, mainChannelId, showSuccess, isRtl, t]);

  // Handle start import
  const handleStartImport = useCallback((propertyIds: string[]) => {
    if (!userChannelId || apiChannelType === null) {
      showError(t(T_KEYS.CHANNEL_DATA_NOT_AVAILABLE));
      return;
    }

    setImportingIds(propertyIds);
    let apiCompleted = false;

    importUnits(
      { 
        userChannelId, 
        channelType: apiChannelType, 
        listings: unitsData?.units.filter(unit => propertyIds.includes(unit.id)).map(unit => ({
          listingId: unit.id, 
          listingName: unit.name, 
          imageUrl: unit.imageUrl ?? null
        })) || []
      },
      {
        onSuccess: (result) => {
          apiCompleted = true;
          setImportingIds([]);
          setProperties((prev) =>
            prev.map((p) =>
              propertyIds.includes(p.id) ? { ...p, isImported: true } : p
            )
          );
          const successMessage = isRtl ? result.messageAr : result.message;
          showSuccess(successMessage || t('CHANNELS.IMPORT_SUCCESS'));
          onSuccess?.();
          onClose();
        },
        onError: (error) => {
          apiCompleted = true;
          setImportingIds([]);
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMessage = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message)
            : errorWithAr.message;
          showError(errorMessage || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );

    setTimeout(() => {
      if (!apiCompleted) {
        setShowImportingModal(true);
      }
    }, 2000);
  }, [userChannelId, apiChannelType, importUnits, unitsData, showSuccess, showError, t, isRtl, onSuccess, onClose]);

  // Handle import later (close modal)
  const handleImportLater = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  // Handle go to dashboard from importing modal
  const handleGoToDashboard = useCallback(() => {
    setShowImportingModal(false);
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  // Handle stay here from importing modal
  const handleModalStayHere = useCallback(() => {
    setShowImportingModal(false);
    setImportingIds([]);
  }, []);

  // Get modal title
  const getModalTitle = () => {
    if (currentStep === 'import') {
      return t(T_KEYS.IMPORT_UNITS_MODAL_TITLE);
    }
    return t(T_KEYS.ADD_NEW_PROPERTY_TITLE);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="add-booking-property-modal"
      >
        <ModalContainer $isRtl={isRtl}>
          <ModalHeader $isRtl={isRtl}>
            <ModalTitle>{getModalTitle()}</ModalTitle>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </ModalHeader>

          <ModalContent>
            {currentStep === 'propertyId' && (
              <BookingPropertyIdStep
                unitType={unitType}
                onSubmit={handleBookingPropertyIdsSubmit}
                onSubmitSingle={handleBookingSinglePropertyIdSubmit}
                isLoading={unitType === 'single' ? isValidatingBookingIds : isCheckingBooking}
                error={bookingError}
                validationResults={bookingValidationResults}
                hasValidationErrors={hasBookingValidationErrors}
              />
            )}

            {currentStep === 'import' && (
              <ImportPropertiesStep
                channelName="booking"
                properties={properties}
                onStartImport={handleStartImport}
                onImportLater={handleImportLater}
                isLoading={isUnitsLoading}
                importingIds={importingIds}
                isImportButtonLoading={isImporting}
              />
            )}
          </ModalContent>
        </ModalContainer>
      </Modal>

      <ImportingModal
        open={showImportingModal}
        onGoToDashboard={handleGoToDashboard}
        onStayHere={handleModalStayHere}
      />
    </>
  );
};

export default AddBookingPropertyModal;

// Styled Components
const ModalContainer = styled(Box)<{ $isRtl: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  background: #f5f5f5;
  border-radius: 24px;
  outline: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  direction: ${({ $isRtl }) => ($isRtl ? 'rtl' : 'ltr')};
`;

const ModalHeader = styled.div<{ $isRtl: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: ${({ $isRtl }) => ($isRtl ? 'row-reverse' : 'row')};
  padding: 20px 24px;
  background: white;
  border-bottom: 1px solid #e6e6e6;
`;

const ModalTitle = styled.h2`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #262626;
  margin: 0;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;
